import React, { useMemo, useState, useEffect } from 'react';
import { useTable, useGlobalFilter, useSortBy, usePagination } from 'react-table';
import 'bootstrap/dist/css/bootstrap.min.css';
import { faEye, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import './Donors.css';
import { exportToExcel } from '../../../../utils/excelExport';
import { DonationService } from '../../../../Services/Axios/Api';
import { Button, Form } from 'react-bootstrap';

const Donors = () => {
    const [donorData, setDonorData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchDonors = async () => {
            try {
                const response = await DonationService.getDonors();
                if (response.isSuccess) {
                    const formattedData = response.result.map(donor => ({
                        name: donor.name,
                        email: donor.email,
                        phone: donor.phoneNumber,
                        totalDonations: donor.donationCount
                    }));
                    setDonorData(formattedData);
                } else {
                    setError('Failed to fetch donors data');
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchDonors();
    }, []);

    const columns = useMemo(
        () => [
            {
                Header: 'Name',
                accessor: 'name',
            },
            {
                Header: 'Email',
                accessor: 'email',
            },
            {
                Header: 'Phone',
                accessor: 'phone',
            },
            {
                Header: 'Total Donations',
                accessor: 'totalDonations',
            },
            {
                Header: 'Actions',
                Cell: ({ row }) => (
                    <div className="donors-action-buttons">
                        <button 
                            className="btn btn-outline-info btn-sm donors-view-btn" 
                            onClick={() => navigate(`/bj-admin/donations/donors/profile/${row.original.email}`)}
                        >
                            <FontAwesomeIcon icon={faEye} />
                        </button>
                    </div>
                ),
            },
        ],
        []
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        page,
        prepareRow,
        state,
        setGlobalFilter,
        pageOptions,
        canPreviousPage,
        canNextPage,
        nextPage,
        previousPage,
        gotoPage,
        setPageSize,
    } = useTable(
        { 
            columns, 
            data: donorData,
            initialState: {
                sortBy: [{ id: 'totalDonations', desc: true }],
                pageSize: 10,
            }
        },
        useGlobalFilter,
        useSortBy,
        usePagination
    );

    const { globalFilter, pageIndex, pageSize } = state;
    const navigate = useNavigate();

    const handleExport = () => {
        const exportData = donorData.map(donor => ({
            'Name': donor.name,
            'Email': donor.email,
            'Phone': donor.phone,
            'Total Donations': donor.totalDonations
        }));
        
        exportToExcel(exportData, 'donors.xlsx');
    };

    if (loading) {
        return (
            <div className="container mt-5 text-center">
                <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mt-5">
                <div className="alert alert-danger" role="alert">
                    {error}
                </div>
            </div>
        );
    }

    return (
        <div className="container mt-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className='m-2'>Donors</h2>
                <div className='m-2'>
                    <button 
                        className="btn btn-success me-2" 
                        onClick={handleExport}
                    >
                        <FontAwesomeIcon icon={faFileExcel} className="me-2" />
                        Export to Excel
                    </button>
                </div>
            </div>
            <div className="Donors-header d-flex justify-content-end align-items-center mb-4">
                <div className="donors-search-box">
                    <input
                        type="text"
                        className="form-control donors-search-input"
                        placeholder="Search donors..."
                        value={globalFilter || ''}
                        onChange={e => {
                            setGlobalFilter(e.target.value);
                            gotoPage(0);
                        }}
                    />
                </div>
            </div>
            
            <div className="donors-table-container">
                <table className="table table-hover" {...getTableProps()}>
                    <thead className="donors-table-header">
                        {headerGroups.map(headerGroup => {
                            const { key, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps();
                            return (
                                <tr key={key} {...restHeaderGroupProps}>
                                    {headerGroup.headers.map(column => {
                                        const { key: columnKey, ...restHeaderProps } = column.getHeaderProps(column.getSortByToggleProps());
                                        return (
                                            <th 
                                                key={columnKey}
                                                className="donors-table-th" 
                                                {...restHeaderProps}
                                            >
                                                {column.render('Header')}
                                                <span className="donors-sort-indicator">
                                                    {column.isSorted
                                                        ? column.isSortedDesc
                                                            ? ' 🔽'
                                                            : ' 🔼'
                                                        : ''}
                                                </span>
                                            </th>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {page.map(row => {
                            prepareRow(row);
                            const { key, ...restRowProps } = row.getRowProps();
                            return (
                                <tr key={key} {...restRowProps}>
                                    {row.cells.map(cell => {
                                        const { key: cellKey, ...restCellProps } = cell.getCellProps();
                                        return (
                                            <td key={cellKey} {...restCellProps}>
                                                {cell.render('Cell')}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="d-flex flex-column flex-md-row align-items-center justify-content-between mt-3 gap-2">
                <div className="d-flex align-items-center gap-2">
                    <span>Rows per page:</span>
                    <Form.Select
                        size="sm"
                        style={{ width: '90px' }}
                        value={pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                    >
                        {[10, 20, 50, 100].map(size => (
                            <option key={size} value={size}>{size}</option>
                        ))}
                    </Form.Select>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <Button variant="outline-primary" size="sm" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
                        {'<<'}
                    </Button>
                    <Button variant="outline-primary" size="sm" onClick={() => previousPage()} disabled={!canPreviousPage}>
                        {'<'}
                    </Button>
                    <span className="mx-2">
                        Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length}</strong>
                    </span>
                    <Form.Control
                        size="sm"
                        type="number"
                        min={1}
                        max={pageOptions.length}
                        value={pageIndex + 1}
                        onChange={(e) => {
                            const next = e.target.value ? Number(e.target.value) - 1 : 0;
                            gotoPage(next);
                        }}
                        style={{ width: '80px' }}
                    />
                    <Button variant="outline-primary" size="sm" onClick={() => nextPage()} disabled={!canNextPage}>
                        {'>'}
                    </Button>
                    <Button variant="outline-primary" size="sm" onClick={() => gotoPage(pageOptions.length - 1)} disabled={!canNextPage}>
                        {'>>'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default Donors;
